import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import toast from 'react-hot-toast';

const AuthPage = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [userType, setUserType] = useState('college');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    profile: {
      firstName: '',
      lastName: '',
      phone: ''
    },
    collegeCode: '',
    companyName: ''
  });
  const [errors, setErrors] = useState({});
  const { login, register, loading } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('profile.')) {
      const profileField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          [profileField]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (activeTab === 'register' && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (activeTab === 'register') {
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }

      if (!formData.profile.firstName) {
        newErrors.firstName = 'First name is required';
      }

      if (!formData.profile.lastName) {
        newErrors.lastName = 'Last name is required';
      }

      if (userType === 'college' && !formData.collegeCode) {
        newErrors.collegeCode = 'College code is required';
      }

      if (userType === 'company' && !formData.companyName) {
        newErrors.companyName = 'Company name is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (activeTab === 'login') {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        toast.error(result.message);
      }
    } else {
      // Prepare registration data based on user type
      const registrationData = {
        email: formData.email,
        password: formData.password,
        profile: {
          firstName: formData.profile.firstName,
          lastName: formData.profile.lastName,
          phone: formData.profile.phone || ''
        },
        role: userType === 'college' ? 'ADMIN' : 'VERIFIER'
      };

      // Add collegeCode or companyName based on userType
      if (userType === 'college') {
        registrationData.collegeCode = formData.collegeCode;
      } else {
        registrationData.companyName = formData.companyName;
      }

      console.log('Sending registration data:', registrationData); // Debug log

      const result = await register(registrationData);
      if (result.success) {
        toast.success('Registration successful!');
        navigate('/dashboard');
      } else {
        toast.error(result.message || 'Registration failed');
        if (result.errors && Array.isArray(result.errors)) {
          result.errors.forEach(err => {
            toast.error(`${err.path || err.param}: ${err.msg}`);
          });
        }
      }
    }
  };

  return (
      <div className="auth-page" style={{ minHeight: '100vh', display: 'flex' }}>
        <div
            className={`auth-section ${userType === 'college' ? 'active' : ''}`}
            style={{
              width: '50%',
              background: userType === 'college' ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' : '#f8fafc',
              color: userType === 'college' ? 'white' : '#64748b',
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              border: userType === 'college' ? 'none' : '2px solid #dbeafe'
            }}
            onClick={() => setUserType('college')}
        >
          <div className="text-center">
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎓</div>
            <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem' }}>
              Educational Institution
            </h2>
            <p style={{ fontSize: '1.1rem', opacity: '0.9', marginBottom: '2rem' }}>
              Issue and manage student certificates on blockchain
            </p>

            {userType === 'college' && (
                <div className="auth-form-container">
                  <div className="auth-tabs" style={{ marginBottom: '2rem' }}>
                    <button
                        type="button"
                        className={`tab-btn ${activeTab === 'login' ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab('login');
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          margin: '0 0.5rem',
                          background: activeTab === 'login' ? 'rgba(255,255,255,0.2)' : 'transparent',
                          border: '1px solid rgba(255,255,255,0.3)',
                          color: 'white',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                    >
                      Login
                    </button>
                    <button
                        type="button"
                        className={`tab-btn ${activeTab === 'register' ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab('register');
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          margin: '0 0.5rem',
                          background: activeTab === 'register' ? 'rgba(255,255,255,0.2)' : 'transparent',
                          border: '1px solid rgba(255,255,255,0.3)',
                          color: 'white',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                    >
                      Register
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} style={{ maxWidth: '300px', margin: '0 auto' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ marginBottom: '1rem' }}>
                      <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Email Address"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid rgba(255,255,255,0.3)',
                            borderRadius: '4px',
                            background: 'rgba(255,255,255,0.1)',
                            color: 'white',
                            fontSize: '1rem'
                          }}
                          disabled={loading}
                      />
                      {errors.email && <span style={{ color: '#fecaca', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>{errors.email}</span>}
                    </div>

                    {activeTab === 'register' && (
                        <>
                          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                            <div style={{ flex: 1 }}>
                              <input
                                  type="text"
                                  name="profile.firstName"
                                  value={formData.profile.firstName}
                                  onChange={handleChange}
                                  placeholder="First Name"
                                  style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    borderRadius: '4px',
                                    background: 'rgba(255,255,255,0.1)',
                                    color: 'white',
                                    fontSize: '1rem'
                                  }}
                                  disabled={loading}
                              />
                              {errors.firstName && <span style={{ color: '#fecaca', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>{errors.firstName}</span>}
                            </div>
                            <div style={{ flex: 1 }}>
                              <input
                                  type="text"
                                  name="profile.lastName"
                                  value={formData.profile.lastName}
                                  onChange={handleChange}
                                  placeholder="Last Name"
                                  style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    borderRadius: '4px',
                                    background: 'rgba(255,255,255,0.1)',
                                    color: 'white',
                                    fontSize: '1rem'
                                  }}
                                  disabled={loading}
                              />
                              {errors.lastName && <span style={{ color: '#fecaca', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>{errors.lastName}</span>}
                            </div>
                          </div>

                          <div style={{ marginBottom: '1rem' }}>
                            <input
                                type="text"
                                name="collegeCode"
                                value={formData.collegeCode}
                                onChange={handleChange}
                                placeholder="College Code (e.g., MIT)"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '1px solid rgba(255,255,255,0.3)',
                                  borderRadius: '4px',
                                  background: 'rgba(255,255,255,0.1)',
                                  color: 'white',
                                  fontSize: '1rem'
                                }}
                                disabled={loading}
                            />
                            {errors.collegeCode && <span style={{ color: '#fecaca', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>{errors.collegeCode}</span>}
                          </div>
                        </>
                    )}

                    <div style={{ marginBottom: '1rem' }}>
                      <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Password"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid rgba(255,255,255,0.3)',
                            borderRadius: '4px',
                            background: 'rgba(255,255,255,0.1)',
                            color: 'white',
                            fontSize: '1rem'
                          }}
                          disabled={loading}
                      />
                      {errors.password && <span style={{ color: '#fecaca', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>{errors.password}</span>}
                    </div>

                    {activeTab === 'register' && (
                        <div style={{ marginBottom: '1rem' }}>
                          <input
                              type="password"
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleChange}
                              placeholder="Confirm Password"
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid rgba(255,255,255,0.3)',
                                borderRadius: '4px',
                                background: 'rgba(255,255,255,0.1)',
                                color: 'white',
                                fontSize: '1rem'
                              }}
                              disabled={loading}
                          />
                          {errors.confirmPassword && <span style={{ color: '#fecaca', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>{errors.confirmPassword}</span>}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: 'rgba(255,255,255,0.2)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          borderRadius: '4px',
                          color: 'white',
                          fontSize: '1rem',
                          fontWeight: '600',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s ease',
                          opacity: loading ? 0.6 : 1
                        }}
                    >
                      {loading ? 'Processing...' : (activeTab === 'login' ? 'Login' : 'Register')}
                    </button>
                  </form>
                </div>
            )}
          </div>
        </div>

        {/* Company Section */}
        <div
            className={`auth-section ${userType === 'company' ? 'active' : ''}`}
            style={{
              width: '50%',
              background: userType === 'company' ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' : '#f8fafc',
              color: userType === 'company' ? 'white' : '#64748b',
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              border: userType === 'company' ? 'none' : '2px solid #dbeafe'
            }}
            onClick={() => setUserType('company')}
        >
          <div className="text-center">
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🏢</div>
            <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem' }}>
              Company Verifier
            </h2>
            <p style={{ fontSize: '1.1rem', opacity: '0.9', marginBottom: '2rem' }}>
              Verify employee credentials and background checks
            </p>

            {userType === 'company' && (
                <div className="auth-form-container">
                  <div className="auth-tabs" style={{ marginBottom: '2rem' }}>
                    <button
                        type="button"
                        className={`tab-btn ${activeTab === 'login' ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab('login');
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          margin: '0 0.5rem',
                          background: activeTab === 'login' ? 'rgba(255,255,255,0.2)' : 'transparent',
                          border: '1px solid rgba(255,255,255,0.3)',
                          color: 'white',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                    >
                      Login
                    </button>
                    <button
                        type="button"
                        className={`tab-btn ${activeTab === 'register' ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab('register');
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          margin: '0 0.5rem',
                          background: activeTab === 'register' ? 'rgba(255,255,255,0.2)' : 'transparent',
                          border: '1px solid rgba(255,255,255,0.3)',
                          color: 'white',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                    >
                      Register
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} style={{ maxWidth: '300px', margin: '0 auto' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ marginBottom: '1rem' }}>
                      <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Email Address"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid rgba(255,255,255,0.3)',
                            borderRadius: '4px',
                            background: 'rgba(255,255,255,0.1)',
                            color: 'white',
                            fontSize: '1rem'
                          }}
                          disabled={loading}
                      />
                      {errors.email && <span style={{ color: '#fecaca', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>{errors.email}</span>}
                    </div>

                    {activeTab === 'register' && (
                        <>
                          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                            <div style={{ flex: 1 }}>
                              <input
                                  type="text"
                                  name="profile.firstName"
                                  value={formData.profile.firstName}
                                  onChange={handleChange}
                                  placeholder="First Name"
                                  style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    borderRadius: '4px',
                                    background: 'rgba(255,255,255,0.1)',
                                    color: 'white',
                                    fontSize: '1rem'
                                  }}
                                  disabled={loading}
                              />
                              {errors.firstName && <span style={{ color: '#fecaca', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>{errors.firstName}</span>}
                            </div>
                            <div style={{ flex: 1 }}>
                              <input
                                  type="text"
                                  name="profile.lastName"
                                  value={formData.profile.lastName}
                                  onChange={handleChange}
                                  placeholder="Last Name"
                                  style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    borderRadius: '4px',
                                    background: 'rgba(255,255,255,0.1)',
                                    color: 'white',
                                    fontSize: '1rem'
                                  }}
                                  disabled={loading}
                              />
                              {errors.lastName && <span style={{ color: '#fecaca', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>{errors.lastName}</span>}
                            </div>
                          </div>

                          <div style={{ marginBottom: '1rem' }}>
                            <input
                                type="text"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                placeholder="Company Name"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '1px solid rgba(255,255,255,0.3)',
                                  borderRadius: '4px',
                                  background: 'rgba(255,255,255,0.1)',
                                  color: 'white',
                                  fontSize: '1rem'
                                }}
                                disabled={loading}
                            />
                            {errors.companyName && <span style={{ color: '#fecaca', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>{errors.companyName}</span>}
                          </div>
                        </>
                    )}

                    <div style={{ marginBottom: '1rem' }}>
                      <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Password"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid rgba(255,255,255,0.3)',
                            borderRadius: '4px',
                            background: 'rgba(255,255,255,0.1)',
                            color: 'white',
                            fontSize: '1rem'
                          }}
                          disabled={loading}
                      />
                      {errors.password && <span style={{ color: '#fecaca', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>{errors.password}</span>}
                    </div>

                    {activeTab === 'register' && (
                        <div style={{ marginBottom: '1rem' }}>
                          <input
                              type="password"
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleChange}
                              placeholder="Confirm Password"
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid rgba(255,255,255,0.3)',
                                borderRadius: '4px',
                                background: 'rgba(255,255,255,0.1)',
                                color: 'white',
                                fontSize: '1rem'
                              }}
                              disabled={loading}
                          />
                          {errors.confirmPassword && <span style={{ color: '#fecaca', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>{errors.confirmPassword}</span>}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: 'rgba(255,255,255,0.2)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          borderRadius: '4px',
                          color: 'white',
                          fontSize: '1rem',
                          fontWeight: '600',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s ease',
                          opacity: loading ? 0.6 : 1
                        }}
                    >
                      {loading ? 'Processing...' : (activeTab === 'login' ? 'Login' : 'Register')}
                    </button>
                  </form>
                </div>
            )}
          </div>
        </div>
      </div>
  );
};

export default AuthPage;